import { z } from "zod";
import { isoTimestamp } from "./common.js";

/** Longest page URL a step record carries (matches the cloud column). */
export const STEP_URL_MAX_LENGTH = 4096;
/** Longest screen name (`surface`) a step record carries. */
export const STEP_SURFACE_MAX_LENGTH = 200;

/**
 * Shapes for step and fingerprint records under `iamu/.runs/<id>/`.
 *
 * `steps/NNN.json` — one file per step (older runs: `steps.jsonl`, one
 * step per line). Mirrors the cloud `walkthrough_steps` table for fields
 * the walker writes during a walkthrough. Additional fields
 * (tool-specific metadata) MAY be attached under `extra`.
 *
 * `fingerprints.jsonl` — one fingerprint per line. Keyed by `step_number`.
 *
 * @example
 * ```ts
 * const step = runStepSchema.parse({
 *   step_number: 1,
 *   timestamp: "2026-09-26T10:00:00Z",
 *   action: "Opened the search page",
 *   url: "https://shop.example/search?q=tv",
 *   surface: "search results",
 * });
 * ```
 *
 * @public
 */
export const runStepSchema = z.object({
  step_number: z.number().int().nonnegative(),
  timestamp: isoTimestamp,
  /** Free-form action label (click, type, navigate, observe, etc.). */
  action: z.string().min(1),
  /** Short human-readable note describing what the persona did and why. */
  note: z.string().nullish(),
  /**
   * The page the persona was on when the step ended — the browser's
   * current URL. Required for new runs; optional so older runs, which
   * never recorded it, still parse.
   */
  url: z.string().url().max(STEP_URL_MAX_LENGTH).nullish(),
  /**
   * The walker's short name for the screen, in the product's own terms
   * (e.g. `search results`, `checkout — payment`). Optional.
   */
  surface: z.string().min(1).max(STEP_SURFACE_MAX_LENGTH).nullish(),
  /** Optional citations or verification notes for this step. */
  grounding: z.string().nullish(),
  /** Optional screenshot filename relative to `iamu/.runs/<id>/screenshots/`. */
  screenshot: z.string().nullish(),
  /** Optional fingerprint line id; paired via `step_number` with `fingerprints.jsonl`. */
  fingerprint_id: z.string().nullish(),
  /** Catch-all for tool-specific payload (DOM snippet, error object, etc.). */
  extra: z.record(z.string(), z.unknown()).nullish(),
});
export type RunStep = z.infer<typeof runStepSchema>;

export const runFingerprintSchema = z.object({
  step_number: z.number().int().nonnegative(),
  timestamp: isoTimestamp,
  dom_hash: z.string().nullish(),
  ax_hash: z.string().nullish(),
  screenshot_phash: z.string().nullish(),
  network: z.array(z.unknown()).nullish(),
  console: z.array(z.unknown()).nullish(),
  soft_fails: z.array(z.string()).nullish(),
});
export type RunFingerprint = z.infer<typeof runFingerprintSchema>;
